import { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, ArrowLeft, X } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { workOrdersApi, type ChatThreadItem } from '@/api/workOrdersApi';
import { useSignalR } from '@/hooks/useSignalR';
import { FrontDeskChatPanel } from './FrontDeskChatPanel';
import type { ChatMessage } from './ChatThread';
import { useAuthStore } from '@/store/authStore';
import { useNotificationSound } from '@/hooks/useNotificationSound';
import Toast from '@/components/ui/Toast';

export function ChatCenter() {
  const [open, setOpen] = useState(false);
  const [threads, setThreads] = useState<ChatThreadItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [selected, setSelected] = useState<ChatThreadItem | null>(null);
  const [totalUnread, setTotalUnread] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const { on, off } = useSignalR();
  const staffId = useAuthStore((state) => state.staffId);
  const { playNotificationSound } = useNotificationSound();
  const [liveAlert, setLiveAlert] = useState<string | null>(null);
  const unreadRef = useRef(0);
  const hasLoadedRef = useRef(false);

  const loadThreads = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await workOrdersApi.getChatThreads();
      const nextUnread = data.reduce((sum, thread) => sum + thread.unreadCount, 0);
      setThreads(data);
      setTotalUnread(nextUnread);
      if (hasLoadedRef.current && nextUnread > unreadRef.current) {
        setLiveAlert('New workshop chat message');
        void playNotificationSound();
      }
      unreadRef.current = nextUnread;
      hasLoadedRef.current = true;
    } catch {
      setLoadError('Shop conversations could not be loaded. Please retry.');
    } finally {
      setLoading(false);
    }
  }, [playNotificationSound]);

  // Initial load
  useEffect(() => {
    loadThreads();
    const pollId = window.setInterval(loadThreads, 10_000);
    return () => window.clearInterval(pollId);
  }, [loadThreads]);

  // Real-time: new message → refresh thread list unread counts + reorder
  useEffect(() => {
    const handler = (...args: unknown[]) => {
      const workOrderId = args[0] as number;
      const msg = args[1] as ChatMessage;
      const managerRoles = new Set(['Owner', 'Manager', 'ServiceAdvisor']);
      const isIncoming = staffId != null
        ? msg.senderStaffId !== staffId
        : !managerRoles.has(msg.senderRole);

      if (isIncoming) {
        setLiveAlert(`New message from ${msg.senderName || 'the workshop floor'}`);
        void playNotificationSound();
      }

      setThreads((prev) => {
        const existing = prev.find((t) => t.workOrderId === workOrderId);
        if (existing) {
          const isOpen = selected?.workOrderId === workOrderId;
          const updated: ChatThreadItem = {
            ...existing,
            lastMessagePreview: msg.messageText.length > 60
              ? msg.messageText.slice(0, 60) + '…'
              : msg.messageText,
            lastSenderName: msg.senderName,
            lastMessageAt: msg.sentAt,
            unreadCount: isOpen ? existing.unreadCount : existing.unreadCount + 1,
          };
          const others = prev.filter((t) => t.workOrderId !== workOrderId);
          if (!isOpen) {
            unreadRef.current += 1;
            setTotalUnread((n) => n + 1);
          }
          return [updated, ...others];
        }
        // Unknown thread — refresh full list
        unreadRef.current += 1;
        loadThreads();
        return prev;
      });
    };

    on('NewChatMessage', handler);
    return () => off('NewChatMessage', handler);
  }, [selected, on, off, loadThreads, staffId, playNotificationSound]);

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  function handleSelectThread(thread: ChatThreadItem) {
    setSelected(thread);
    // Clear that thread's unread locally
    setThreads((prev) =>
      prev.map((t) =>
        t.workOrderId === thread.workOrderId ? { ...t, unreadCount: 0 } : t
      )
    );
    setTotalUnread((n) => Math.max(0, n - thread.unreadCount));
    unreadRef.current = Math.max(0, unreadRef.current - thread.unreadCount);
  }

  function handleBack() {
    setSelected(null);
    loadThreads(); // refresh counts after reading
  }

  function handleOpenPanel() {
    setOpen((o) => !o);
    if (!open) loadThreads();
  }

  return (
    <div className="relative" ref={panelRef}>
      {liveAlert && <Toast message={liveAlert} type="info" duration={6000} onClose={() => setLiveAlert(null)} />}
      {/* Icon button */}
      <button
        onClick={handleOpenPanel}
        className="relative p-2 rounded-xl bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-white/20 transition-colors"
        aria-label="Shop chat"
      >
        <MessageCircle className="h-5 w-5" />
        {totalUnread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
            {totalUnread > 99 ? '99+' : totalUnread}
          </span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-white dark:bg-surface-dark border border-slate-200 dark:border-border-dark rounded-2xl shadow-xl z-50 flex flex-col overflow-hidden"
          style={{ height: '480px' }}>

          {/* Panel header */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 dark:border-border-dark flex-shrink-0">
            {selected ? (
              <button
                onClick={handleBack}
                className="p-1 -ml-1 rounded-lg hover:bg-slate-100 dark:hover:bg-white/10 transition-colors text-slate-500 hover:text-slate-800 dark:hover:text-white"
                aria-label="Back to thread list"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            ) : null}
            <h3 className="flex-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
              {selected
                ? `${selected.vehicleInfo} · RO #${selected.workOrderNumber}`
                : 'Shop Chat'}
            </h3>
            <button
              onClick={() => setOpen(false)}
              className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-white/10 transition-colors text-slate-400 hover:text-slate-600 dark:hover:text-white"
              aria-label="Close chat"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Panel body */}
          <div className="flex-1 min-h-0">
            {selected ? (
              <FrontDeskChatPanel
                workOrderId={selected.workOrderId}
                vehicleInfo={selected.vehicleInfo}
                roNumber={selected.workOrderNumber}
                techName={selected.assignedTechnicianName}
                onNewMessage={() => {
                  // Bump totalUnread only if this panel is still selected
                  // (new message arrived from someone else while we're viewing)
                  setTotalUnread((n) => n); // no-op; message is being seen live
                }}
              />
            ) : (
              <ThreadList
                threads={threads}
                loading={loading}
                error={loadError}
                onRetry={loadThreads}
                onSelect={handleSelectThread}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Thread List sub-component ─────────────────────────────────────────────

interface ThreadListProps {
  threads: ChatThreadItem[];
  loading: boolean;
  error: string;
  onRetry: () => void;
  onSelect: (t: ChatThreadItem) => void;
}

function ThreadList({ threads, loading, error, onRetry, onSelect }: ThreadListProps) {
  if (loading && threads.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-slate-400 text-sm">
        Loading threads…
      </div>
    );
  }

  if (error) {
    return <div className="flex h-40 flex-col items-center justify-center gap-3 px-6 text-center"><p className="text-sm font-semibold text-red-700">{error}</p><button onClick={onRetry} className="rounded-lg bg-slate-900 px-4 py-2 text-xs font-bold text-white">Retry</button></div>;
  }

  if (threads.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-32 text-slate-400 text-sm gap-2">
        <MessageCircle className="h-8 w-8 opacity-40" />
        No active chat threads
      </div>
    );
  }

  return (
    <div className="overflow-y-auto h-full">
      {threads.map((t) => (
        <button
          key={t.workOrderId}
          onClick={() => onSelect(t)}
          className="w-full text-left px-4 py-3 border-b border-slate-100 dark:border-border-dark hover:bg-slate-50 dark:hover:bg-white/5 transition-colors flex items-start gap-3 last:border-0"
        >
          {/* Avatar / unread indicator */}
          <div className="flex-shrink-0 mt-0.5">
            {t.unreadCount > 0 ? (
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary-600 text-white text-xs font-bold">
                {t.unreadCount > 9 ? '9+' : t.unreadCount}
              </span>
            ) : (
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 dark:bg-white/10 text-slate-400">
                <MessageCircle className="h-4 w-4" />
              </span>
            )}
          </div>

          {/* Thread info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-2">
              <p className={`text-sm truncate ${t.unreadCount > 0 ? 'font-bold text-slate-900 dark:text-white' : 'font-medium text-slate-700 dark:text-slate-300'}`}>
                {t.vehicleInfo}
              </p>
              <span className="text-[10px] text-slate-400 flex-shrink-0">
                {formatDistanceToNow(new Date(t.lastMessageAt), { addSuffix: true })}
              </span>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">
              RO #{t.workOrderNumber}
              {t.assignedTechnicianName ? ` · ${t.assignedTechnicianName}` : ''}
            </p>
            <p className={`text-xs truncate mt-1 ${t.unreadCount > 0 ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400'}`}>
              {t.lastSenderName ? `${t.lastSenderName}: ` : ''}{t.lastMessagePreview}
            </p>
          </div>
        </button>
      ))}
    </div>
  );
}
